import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ShoppingCart, Check } from "lucide-react";
import api from "@/api/client";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState({});
  const { addItem, addItemMulti, setIsOpen } = useCart();

  useEffect(() => {
    api.get(`/products/${slug}`)
      .then(({ data }) => setProduct(data))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-[73px]">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8">
          <div className="aspect-square animate-pulse border border-white/10 bg-surface" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-[73px]">
        <div className="mx-auto max-w-7xl px-4 py-24 text-center sm:px-8">
          <h1 className="font-display text-4xl font-bold uppercase text-white" data-testid="product-not-found">Part Not Found</h1>
          <Link to="/shop" className="mt-6 inline-block font-mono text-sm uppercase tracking-widest text-volt" data-testid="back-to-shop">
            Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  const gallery = product.images?.length ? product.images : [product.image_url];
  const needsOption = product.options?.length > 0;
  const groups = product.option_groups?.length ? product.option_groups : (needsOption ? [{ label: product.options_label || "Select Option", choices: product.options }] : []);
  const allGroupsSelected = groups.every((g) => selectedGroups[g.label]);

  const choiceName = (c) => (typeof c === "object" ? c.name : c);
  const choiceMod = (c) => (typeof c === "object" && c.price_modifier ? c.price_modifier : 0);

  let displayPrice = product.price;
  for (const g of groups) {
    const chosen = selectedGroups[g.label];
    if (!chosen) continue;
    for (const c of g.choices || []) {
      if (choiceName(c) === chosen) displayPrice += choiceMod(c);
    }
  }

  const handleAdd = () => {
    if (!allGroupsSelected) {
      const missing = groups.find((g) => !selectedGroups[g.label]);
      toast.error(`Please select ${missing.label} first`);
      return;
    }
    addItemMulti(product, quantity, groups.length ? selectedGroups : null);
    setIsOpen(true);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <div className="pt-[73px]">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8">
        <Link to="/shop" className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-widest text-slate-400 hover:text-volt" data-testid="back-link">
          <ArrowLeft className="h-4 w-4" /> Back to Shop
        </Link>

        <div className="mt-8 grid gap-12 lg:grid-cols-2">
          <div>
            <div className="relative aspect-square overflow-hidden border border-white/10 bg-surface" data-testid="product-image-container">
              <img src={gallery[activeImage]} alt={product.name} className="h-full w-full object-cover" />
              {product.badge && (
                <span className="absolute left-4 top-4 bg-volt px-3 py-1 font-mono text-sm font-bold uppercase text-obsidian" data-testid="product-badge">
                  {product.badge}
                </span>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="mt-3 flex gap-3 overflow-x-auto" data-testid="product-gallery">
                {gallery.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    className={`h-20 w-20 flex-shrink-0 overflow-hidden border transition-colors ${activeImage === i ? "border-volt" : "border-white/10 hover:border-white/40"}`}
                    data-testid={`gallery-thumb-${i}`}
                  >
                    <img src={img} alt={`${product.name} ${i + 1}`} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col justify-center">
            <p className="font-mono text-sm uppercase tracking-widest text-volt">{product.category}</p>
            <h1 className="mt-2 font-display text-4xl font-bold uppercase tracking-tight text-white sm:text-5xl" data-testid="product-name">
              {product.name}
            </h1>
            <p className="mt-4 font-mono text-3xl font-bold text-volt" data-testid="product-price">
              ${displayPrice.toFixed(2)}
            </p>
            <p className="mt-6 text-slate-300" data-testid="product-description">{product.description}</p>

            {product.compatibility?.length > 0 && (
              <div className="mt-6" data-testid="product-fitment">
                <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Fits</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {product.compatibility.map((fit) => (
                    <span key={fit} className="flex items-center gap-1 border border-white/10 bg-surface px-3 py-1 font-mono text-xs uppercase text-slate-300" data-testid={`fitment-${fit.toLowerCase().replace(/\s+/g, "-")}`}>
                      <Check className="h-3 w-3 text-volt" /> {fit}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {groups.map((g) => (
              <div className="mt-6" key={g.label} data-testid={`product-options-${g.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <p className="font-mono text-xs uppercase tracking-widest text-slate-500">
                  {g.label} {selectedGroups[g.label] ? "" : <span className="text-red-400">* required</span>}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {g.choices.map((c) => {
                    const name = choiceName(c);
                    const mod = choiceMod(c);
                    return (
                      <button
                        key={name}
                        onClick={() => setSelectedGroups({ ...selectedGroups, [g.label]: name })}
                        className={`border px-4 py-2 font-mono text-xs uppercase tracking-widest transition-colors ${selectedGroups[g.label] === name ? "border-volt bg-volt/10 text-volt" : "border-white/10 bg-surface text-slate-300 hover:border-volt/50"}`}
                        data-testid={`option-${g.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        {name}{mod > 0 && <span className="ml-1 text-volt">+${mod}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center border border-white/10 bg-surface">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-4 py-3 font-mono text-lg text-slate-400 hover:text-volt"
                  data-testid="quantity-decrease"
                >
                  −
                </button>
                <span className="w-12 text-center font-mono text-lg text-white" data-testid="quantity-value">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="px-4 py-3 font-mono text-lg text-slate-400 hover:text-volt"
                  data-testid="quantity-increase"
                >
                  +
                </button>
              </div>
              <button
                onClick={handleAdd}
                disabled={product.stock === 0}
                className="flex flex-1 items-center justify-center gap-2 bg-volt py-3 font-mono text-sm font-bold uppercase tracking-widest text-obsidian transition-colors hover:bg-white disabled:opacity-30"
                data-testid="add-to-cart-detail"
              >
                <ShoppingCart className="h-4 w-4" />
                Add to Cart
              </button>
            </div>

            <p className="mt-4 font-mono text-xs uppercase tracking-widest text-slate-500" data-testid="stock-status">
              {product.stock > 0 ? `${product.stock} in stock` : "Out of stock"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
